import { SidebarUsageAlert } from '@/components/sidebar-usage-alert';
import { useWorkspace } from '@/contexts/workspace-context';
import { useCurrentRole } from '@/hooks/use-current-role';

/**
 * Workspace-sidebar usage banner (mounted in app-sidebar.tsx footer, same place
 * as the org banner). Standing counterpart to the threshold toast.
 *
 * Three paused reasons, plus one non-paused band, all keyed off the channels
 * wire's `forwardingDisabledReason`:
 *  - frozen      any channel carries 'usage_limit', 'trial_expired', or
 *                'legacy_expired' (AIT-420: the billing-entitlement gate
 *                reuses the same channels-carried reason as the usage freeze)
 *  - approaching org pool >= 75% and not yet frozen
 *
 * Role-aware (spec D5): admins get an Upgrade Plan CTA to org billing in every
 * band (including freeze — the admin is the one who can unfreeze); members get a
 * read-only explanation and never see billing controls. Layout/palette live in
 * the shared SidebarUsageAlert.
 */
export function WorkspaceUsageNotice() {
  const { channels, orgUsage, activeWorkspace } = useWorkspace();
  const { role } = useCurrentRole();
  const isAdmin = role === 'admin';

  const pausedReason =
    channels.find(
      (c) =>
        c.forwardingDisabledReason === 'usage_limit' ||
        c.forwardingDisabledReason === 'trial_expired' ||
        c.forwardingDisabledReason === 'legacy_expired' ||
        c.forwardingDisabledReason === 'payment_incomplete',
    )?.forwardingDisabledReason ?? null;
  const frozen = pausedReason !== null;
  const percentage = orgUsage?.percentage ?? 0;
  const orgId = activeWorkspace?.organizationPublicId ?? null;
  // AIT-420: this banner is reachable for a v2 org (same org-pooled endpoint),
  // so it must name the org's own meter instead of hardcoding "messages".
  const onActions = orgUsage?.usageUnit === 'actions';

  let message: string;
  if (pausedReason === 'trial_expired') {
    // Members get no CTA, so pointing them at a plan picker they cannot reach
    // is a dead end — name the person who can.
    message = isAdmin
      ? 'Your trial ended. Pick your plan to resume.'
      : 'Your trial ended. Ask your admin to pick a plan.';
  } else if (pausedReason === 'payment_incomplete') {
    // Not "your trial ended" -- their card was declined or they never finished
    // the payment, and the plan picker is the wrong destination (Codex,
    // PR #221). Billing is where the unpaid invoice can be settled.
    message = isAdmin
      ? 'Your payment did not go through. Finish it to resume.'
      : 'Your payment did not go through. Ask your admin to finish it.';
  } else if (pausedReason === 'legacy_expired') {
    message = isAdmin
      ? 'Your old plan ended. Choose a new plan to resume.'
      : 'Your old plan ended. Ask your admin to choose a new plan.';
  } else if (frozen) {
    message = isAdmin
      ? onActions
        ? 'Your organization has used every action in this plan. Your channels are paused.'
        : 'Your organization has reached its message limit. Messages are no longer being sent or received.'
      : onActions
        ? 'Channels paused. The organization used every action in its plan. Contact your admin.'
        : 'Channels paused. The organization reached its message limit. Contact your admin.';
  } else {
    const headline = onActions
      ? `Your organization has used ${percentage}% of this month's actions.`
      : `Your organization is at ${percentage}% of its monthly message limit.`;
    message = isAdmin ? headline : `${headline} Channels may pause soon. Contact your admin.`;
  }

  return (
    <SidebarUsageAlert
      orgId={orgId}
      percentage={percentage}
      frozen={frozen}
      message={message}
      showCta={isAdmin}
      {...(pausedReason === 'payment_incomplete'
        ? ({ ctaTarget: 'billing', ctaLabel: 'Finish Payment' } as const)
        : {})}
    />
  );
}
